/**
 * Eigenbau: Verweis mit Haarlinien-Unterstreichung — 23 Stellen im Entwurf.
 *
 * Der Entwurf unterstreicht Verweise dünn (1px) und mit Abstand (3px), mono und
 * in der Akzentfarbe. Das ist der Grund, warum die globale Regel
 * `a { color: var(--akzent) }` aus dem Entwurf **nicht** übernommen wurde: Sie
 * träfe auch die kopierten Bausteine, die als `<a>` gerenderte Knöpfe
 * enthalten, und würde deren Beschriftung umfärben. Verweise bekommen ihre
 * Farbe hier, an einer Stelle.
 *
 * Kein Tremor-Gegenstück: Tremors `Button` mit `variant="ghost"` wäre ein
 * Knopf ohne Unterstreichung, und ein Verweis ist kein Knopf — er führt
 * woandershin und muss Mittelklick und Kontextmenü können.
 *
 * Dieser Baustein gibt nur die Gestalt. Das Ziel setzt der Aufrufer: `Link` aus
 * dem Router für Routen, `<a>` für Externes, `<button>` nur dort, wo wirklich
 * nichts navigiert.
 */

import type { ReactNode } from "react";
import { cx, focusRing } from "@/lib/utils";

/**
 * Die Klassenliste, damit ein `Link` des Routers die Gestalt annehmen kann,
 * ohne dass ein `<span>` dazwischen liegt — sonst wäre der Fokusrahmen um das
 * eine und die Unterstreichung um das andere Element.
 */
export function verweisKlassen({
  grad = 12,
  tafel = false,
  className,
}: {
  grad?: 12 | 12.5 | 14;
  /**
   * Auf dunklem Grund.
   *
   * `--akzent` ist für helles Papier gemischt; auf der Tafel läuft er zu.
   * Dafür gibt es `--tafel-akzent`. Die **Form** bleibt dieselbe — Mono,
   * Unterstrich, Fokusring —, nur die Tinte wechselt. Ohne diese Variante
   * schrieb die Metarr-Karte sich ihre Verweisklassen von Hand und fiel
   * damit aus dem einen Ort heraus, an dem ein Verweis definiert ist.
   */
  tafel?: boolean;
  className?: string;
} = {}) {
  return cx(
    "font-mono underline decoration-1 underline-offset-[3px]",
    "transition-colors duration-150 ease-aus",
    tafel
      ? "text-tafel-akzent hover:text-tafel-tinte"
      : "text-akzent hover:text-akzenttief",
    focusRing,
    grad === 12 ? "text-[12px]" : grad === 12.5 ? "text-[12.5px]" : "text-sm",
    className,
  );
}

export function Verweis({
  children,
  href,
  grad = 12,
  className,
}: {
  children: ReactNode;
  href: string;
  grad?: 12 | 12.5 | 14;
  className?: string;
}) {
  return (
    <a href={href} className={verweisKlassen({ grad, className })}>
      {children}
    </a>
  );
}

/**
 * Zurückweg, wie der Entwurf ihn auf jeder Detailseite zeigt: Pfeil und Ziel,
 * mono, unterstrichen. Der Pfeil ist dekorativ — er ist keine Auskunft, die
 * ein Vorleseprogramm braucht, „Zurück zu Auto Q&A" steht im Text.
 */
export function Zurueckweg({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cx("inline-flex items-center gap-2", className)}>
      <span aria-hidden="true">←</span>
      {children}
    </span>
  );
}
